import { onMount } from "solid-js";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    fbq?: ((...args: unknown[]) => void) & { getState?: () => { pixels?: { id: string }[] } };
    // GTM registers each loaded container here, keyed by its GTM-… id.
    google_tag_manager?: Record<string, unknown>;
  }
}

export const GOOGLE_TAG_MANAGER = "google_tag_manager";
export const META_PIXEL = "meta_pixel";

export type TrackingPixelConfig = {
  externalService: string;
  pixelId: string;
};

function escapeForScript(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

function injectGoogleTagManager(pixelId: string) {
  try {
    const script = document.createElement("script");
    script.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${escapeForScript(pixelId)}');
    `;
    document.head.appendChild(script);
  } catch {
    /* GTM blocked or failed */
  }

  const noscript = document.createElement("noscript");
  noscript.innerHTML = `<iframe title="Google Tag Manager" src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(pixelId)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.appendChild(noscript);
}

function injectMetaPixel(pixelId: string) {
  const script = document.createElement("script");
  script.textContent = `
    (function() {
      var pixelId = '${escapeForScript(pixelId)}';
      try {
        var s = document.createElement('script');
        s.async = true;
        s.src = 'https://connect.facebook.net/en_US/fbevents.js';
        var loaded = false;
        var timeout = setTimeout(function() {
          if (!loaded) { /* fbevents.js blocked or slow; skip init */ }
        }, 5000);
        s.onload = function() {
          loaded = true;
          clearTimeout(timeout);
          try {
            fbq('init', pixelId);
            fbq('track', 'PageView');
          } catch (e) { /* init failed */ }
        };
        s.onerror = function() { loaded = true; clearTimeout(timeout); };
        document.head.appendChild(s);
      } catch (e) { /* script creation failed */ }
    })();
  `;
  document.head.appendChild(script);

  const noscript = document.createElement("noscript");
  noscript.innerHTML = `<img height="1" width="1" style="display:none;visibility:hidden" src="https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1" alt="">`;
  document.body.appendChild(noscript);
}

function injectPixel(config: TrackingPixelConfig) {
  if (!config.pixelId.trim()) return;
  if (config.externalService === GOOGLE_TAG_MANAGER) {
    injectGoogleTagManager(config.pixelId);
  } else if (config.externalService === META_PIXEL) {
    injectMetaPixel(config.pixelId);
  }
}

type Props = {
  trackingPixels: TrackingPixelConfig[];
};

/**
 * Injects GTM and Meta tag scripts (script + noscript) on mount.
 * Use in any Inertia page that needs to load marketing tags.
 */
export function TrackingPixels(props: Props) {
  onMount(() => {
    props.trackingPixels.forEach(injectPixel);
  });
  return null;
}

/** Whether a configured GTM container or Meta Pixel actually loaded on this page. */
export function trackingPixelLoaded(config: TrackingPixelConfig): boolean {
  try {
    if (config.externalService === GOOGLE_TAG_MANAGER) {
      return Boolean(window.google_tag_manager?.[config.pixelId]);
    }
    if (config.externalService === META_PIXEL) {
      const pixels = window.fbq?.getState?.().pixels ?? [];
      return pixels.some((pixel) => pixel.id === config.pixelId);
    }
  } catch {
    /* a blocked or half-loaded tag reads as not loaded */
  }
  return false;
}

/**
 * Pushes a named event onto the GTM dataLayer queue. Safe to call whether or not
 * a Google Tag Manager container is configured: if none has loaded, the event
 * just sits in (or initializes) the queue and never reaches the network.
 *
 * IMPORTANT: keep payloads free of PII/PHI. This data is exposed to third-party
 * tag vendors (Google, Meta), so never include patient name, email, phone, DOB,
 * or anything that identifies the patient or their care.
 */
export function pushDataLayerEvent(event: string, data?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event, ...data });
}

/**
 * Fires a Meta (Facebook) Pixel event via fbq. A standalone Meta Pixel does not
 * read the GTM dataLayer, so conversions must be sent to it directly. No-ops when
 * no Meta Pixel is configured (fbq undefined) or when the script is blocked.
 *
 * IMPORTANT: keep params free of PII/PHI — this data is sent to Meta.
 */
export function trackMetaConversion(event: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", event, params);
  } catch {
    /* Meta Pixel blocked or failed */
  }
}
